import React, { useContext, useState } from 'react'
import { NavigationHeader } from '@/components/NavigationHeader/NavigationHeader'
import { CompanyTag } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import {
  Client,
  IntroducingBrokerDetailsDto,
} from 'api/Trading-Accounts/Types/IntroducingBrokerDetailsDto'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { useTabNavigate } from 'hooks/useTabNavigate'
import { AuthSessionContext } from 'utils/AuthContext'
import { hasPermission } from 'utils/permissionsValidator'

interface Props {
  data?: IntroducingBrokerDetailsDto
  isEdit: boolean
  tab: IntroducingBrokerTab
}

export enum IntroducingBrokerTab {
  DETAILS = 'details',
  CLIENTS = 'clients',
  TRADING_ACCOUNTS = 'trading-accounts',
  DEALS = 'deals',
  REFERRAL_RESTRICTIONS = 'referral-restrictions',
  CASHBACK = 'cashback',
  REBATES = 'rebates',
  NETWORK = 'network',
}

export const IntroducingBrokerNavigation: React.FC<Props> = (props) => {
  const [auth] = useContext(AuthSessionContext)
  const tabNavigate = useTabNavigate()

  const permissions = auth?.permissions || []

  const { data, isEdit } = props
  const [confirmationModal, setConfirmationModal] = useState<boolean>(props.isEdit)
  const [keyToChange, setKeyToChange] = useState<string>(IntroducingBrokerTab.DETAILS)

  const handleTabClick = (key: string) => {
    setKeyToChange(key)
    if (isEdit) setConfirmationModal(true)
    else tabNavigate(key)
  }

  const handleTabChange = (reason: boolean) => {
    if (reason) {
      tabNavigate(keyToChange)
    }
    setConfirmationModal(false)
  }

  return (
    <div>
      <ConfirmationModal
        contentText='The page is asking you to confirm that you want to leave - data you have enter may not be saved.'
        yesButtonText='Leave Page'
        cancelButtonText='Stay on page'
        onYes={() => handleTabChange(true)}
        onCancel={() => handleTabChange(false)}
        visible={confirmationModal}
      />

      <NavigationHeader>
        {data && <span>{data.name}</span>}
        {props.data?.client && <PartnersCompany client={props.data?.client} />}
      </NavigationHeader>

      <Tabs value={props.tab} onValueChange={handleTabClick} className='mt-2'>
        <div className='w-full border-b'>
          <TabsList>
            {hasPermission(permissions, PermissionsEnum.CanViewTradingAccountOpenPositions) && (
              <TabsTrigger value={IntroducingBrokerTab.DETAILS}>Details</TabsTrigger>
            )}

            {hasPermission(permissions, PermissionsEnum.CanViewTradingAccountOpenPositions) && (
              <TabsTrigger value={IntroducingBrokerTab.CLIENTS}>Clients</TabsTrigger>
            )}

            {hasPermission(permissions, PermissionsEnum.CanViewTradingAccountOpenPositions) && (
              <TabsTrigger value={IntroducingBrokerTab.TRADING_ACCOUNTS}>
                Trading Accounts
              </TabsTrigger>
            )}

            <TabsTrigger value={IntroducingBrokerTab.DEALS}>Deals</TabsTrigger>

            {hasPermission(permissions, PermissionsEnum.CanBrowseIntroducingBrokers) && (
              <TabsTrigger value={IntroducingBrokerTab.REFERRAL_RESTRICTIONS}>
                Referral Restrictions
              </TabsTrigger>
            )}

            {hasPermission(permissions, PermissionsEnum.CanViewIntroducingBrokerRebate) && (
              <TabsTrigger value={IntroducingBrokerTab.REBATES}>Rebates</TabsTrigger>
            )}

            {data?.multiTierRegisteredAt &&
              hasPermission(permissions, PermissionsEnum.CanViewMultiTierNetwork) && (
                <TabsTrigger value={IntroducingBrokerTab.NETWORK}>Network</TabsTrigger>
              )}
          </TabsList>
        </div>
      </Tabs>
    </div>
  )
}

const PartnersCompany = ({ client }: { client: Client }) => (
  <div>
    <CompanyTag id={client.tickmillCompany.id}>{client.tickmillCompany.name}</CompanyTag>
  </div>
)
